import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronRight, ShoppingBag, Heart, Share2 } from "lucide-react";
import { products } from "@/data/products";
import { FarmOriginModule } from "@/components/product/FarmOriginModule";
import { TraceabilityPanel } from "@/components/product/TraceabilityPanel";
import { ImpactBlock } from "@/components/product/ImpactBlock";
import { StoryPreview } from "@/components/product/StoryPreview";
import { ProductInformation } from "@/components/product/ProductInformation";
import { toast } from "sonner";

export default function ProductDetailPage() {
  const { productId } = useParams();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);

  const product = products.find((p) => p.id === productId);

  if (!product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl font-semibold text-foreground">Product not found</h1>
          <p className="text-muted-foreground mt-2">The product you're looking for doesn't exist.</p>
          <Button asChild className="mt-6">
            <Link to="/products">Back to Products</Link>
          </Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    toast.success(`${product.name} added to cart`, {
      description: `Size: ${selectedSize || 'One Size'}, Quantity: ${quantity}`,
    });
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bg-secondary/30 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/products" className="hover:text-foreground transition-colors">Products</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/farm-stories" className="hover:text-foreground transition-colors">Farm Stories</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12">
          {/* Images Column */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="aspect-[4/5] rounded-xl overflow-hidden bg-secondary/30">
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Thumbnail Gallery */}
            {product.images.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === index ? "border-primary" : "border-transparent"
                    }`}
                  >
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details Column */}
          <div className="mt-8 lg:mt-0">
            {/* 1. Title + Price */}
            <div>
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                {product.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="font-normal">
                    {tag}
                  </Badge>
                ))}
              </div>

              <h1 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
                {product.name}
              </h1>
              <p className="text-lg text-muted-foreground mt-2">
                {product.shortDesc}
              </p>
              <p className="text-2xl font-semibold text-foreground mt-4">
                €{product.price}
              </p>
            </div>

            <Separator className="my-6" />

            {/* 2. Farm of Origin Module - Quick Access (SMART Requirement A) */}
            <FarmOriginModule
              farmId={product.farmId}
              farmName={product.farmName}
              farmLocation={product.farmLocation}
              className="mb-6"
            />

            {/* Size & Quantity Selection */}
            <div className="space-y-4 mb-6">
              {/* Size */}
              {product.sizing?.guide && product.sizing.guide.length > 1 && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Size</label>
                  <Select value={selectedSize} onValueChange={setSelectedSize}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {product.sizing.guide.map((sizeOption) => (
                        <SelectItem key={sizeOption.size} value={sizeOption.size}>
                          {sizeOption.size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Quantity */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Quantity</label>
                <Select value={quantity.toString()} onValueChange={(v) => setQuantity(parseInt(v))}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add to Cart & Actions */}
            <div className="flex gap-3">
              <Button size="lg" className="flex-1 gap-2" onClick={handleAddToCart}>
                <ShoppingBag className="h-4 w-4" />
                Add to Cart
              </Button>
              <Button variant="outline" size="lg" className="px-4">
                <Heart className="h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="px-4">
                <Share2 className="h-5 w-5" />
              </Button>
            </div>

            <Separator className="my-8" />

            {/* 3. Traceability Panel (SMART Requirement B) */}
            <TraceabilityPanel
              traceability={product.traceability}
              materialComposition={product.materialComposition}
              className="mb-8"
            />

            {/* 4. Impact Block (SMART Requirement C) */}
            <ImpactBlock impact={product.impact} className="mb-8" />

            {/* 5. Story Preview (SMART Requirement D) */}
            <StoryPreview
              farmId={product.farmId}
              farmName={product.farmName}
              storyPreview={product.storyPreview}
              className="mb-8"
            />

            {/* 6. Product Information Tabs (SMART Requirement E) */}
            <ProductInformation sizing={product.sizing} care={product.care} />
          </div>
        </div>
      </div>
    </div>
  );
}
